import { Injectable, inject } from '@angular/core';

import { OptionGroupType } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventParticipant } from '@frontend/sports/betting-offer/feature/model';
import { MarketGroupingConfig } from '@frontend/sports/common/client-config-data-access';
import { BaseCdsApi, BaseCdsApiFactory, CDS_API_FACTORY } from '@frontend/sports/content-distribution/feature';
import { Store } from '@ngrx/store';
import { Observable, map } from 'rxjs';

import { setPlayerStatisticsOpenAndTrackingStatus } from './player-statistics.actions';
import {
    PlayerStatistics,
    PlayerStatisticsContent,
    PlayerStatisticsPlayerInfo,
    PlayerStatisticsResponse,
    StatisticsInput,
    UpdateStatisticsStoreParams,
} from './player-statistics.model';

@Injectable({ providedIn: 'root' })
export class PlayerStatisticsService {
    marketGroupingConfig = inject(MarketGroupingConfig);
    apiFactory: BaseCdsApiFactory = inject(CDS_API_FACTORY);
    cdsApi: BaseCdsApi = this.apiFactory({ endpoint: '/statistics' });
    store = inject(Store);

    fetchPlayerStats(eventId: string, marketFilter: string, mainMarket: boolean): Observable<PlayerStatistics | undefined> {
        return this.cdsApi.get<PlayerStatisticsResponse>('/player-stats', { eventId, marketFilter, mainMarket }).pipe(
            map((data: PlayerStatisticsResponse | undefined) => {
                const content = data?.content;
                if (!content) {
                    throw new Error(undefined);
                }
                return this.reMapPlayerStatisticsContent(content);
            }),
        );
    }

    getPlayerStatisticsGraph(storeContent: PlayerStatistics | undefined, playerInfo: StatisticsInput): string | undefined {
        if (!storeContent) return undefined;

        if (storeContent.mainMarketUrl) {
            return storeContent.mainMarketUrl;
        }

        const player = storeContent.playerStatistics?.find(({ marketId }) => marketId === playerInfo.marketId);

        if (player?.outcomeId === undefined) return undefined;

        return player && storeContent.graphPropsUrl
            ? `${storeContent.graphPropsUrl}/${playerInfo.eventId}/${player.marketId}/${player.outcomeId}`
            : undefined;
    }

    isPlayerStatisticsVisible(leagueId: number, optionGroup: OptionGroupType): boolean {
        const playerStatsEnabledGroups = this.marketGroupingConfig.playerStatsEnabledOptionGroup ?? {};
        return (!!leagueId && playerStatsEnabledGroups[leagueId]?.includes(optionGroup)) || false;
    }

    isPlayer(players: EventParticipant[], name: string): boolean {
        return players.some((player: EventParticipant) => player.name === name || player.shortName === name);
    }

    getPlayerStatistics(storeContent: PlayerStatistics | undefined, optionGroupId: string): PlayerStatisticsPlayerInfo | undefined {
        if (!storeContent) return undefined;
        const statistics = storeContent.playerStatistics?.find(({ marketId }) => marketId === optionGroupId);
        return statistics;
    }

    openStatistics(params: UpdateStatisticsStoreParams): void {
        const { eventId, marketFilter, playerName, marketId, isOpen, tracking, mainMarket = false } = params;
        this.store.dispatch(
            setPlayerStatisticsOpenAndTrackingStatus({
                eventId,
                marketFilter,
                mainMarket,
                playerName,
                marketId,
                isOpen,
                tracking,
            }),
        );
    }

    private reMapPlayerStatisticsContent(content: PlayerStatisticsContent): PlayerStatistics {
        const { graph_props_url: graphPropsUrl, main_market_url: mainMarketUrl, player_stats = [] } = content;

        const playerStatistics = player_stats.map(({ player_name, full_name, market_id, outcome_id }) => ({
            playerName: player_name,
            fullName: full_name,
            marketId: market_id,
            outcomeId: outcome_id,
        }));

        return {
            graphPropsUrl,
            mainMarketUrl,
            playerStatistics,
        };
    }
}
